import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DayPickerProps, DateRange } from "react-day-picker";
import { cn } from "../../lib/utils";
import {
  addDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
} from "date-fns";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: DayPickerProps) {
  const isRange = props.mode === "range";
  const onSelect = isRange
    ? (props as DayPickerProps & { mode: "range" }).onSelect
    : undefined;
  const selected = props.selected as DateRange | undefined;

  const handleQuickSelect = (label: string) => {
    if (!isRange || !onSelect) return;

    const today = new Date();
    let range: DateRange = { from: undefined, to: undefined };

    switch (label) {
      case "Today":
        range = { from: today, to: today };
        break;
      case "This Week":
        range = { from: startOfWeek(today), to: endOfWeek(today) };
        break;
      case "Last Week":
        const lastWeekStart = startOfWeek(subWeeks(today, 1));
        range = { from: lastWeekStart, to: endOfWeek(lastWeekStart) };
        break;
      case "Last 7 Days":
        range = { from: addDays(today, -6), to: today };
        break;
      case "Last 30 Days":
        range = { from: addDays(today, -29), to: today };
        break;
      case "This Month":
        range = { from: startOfMonth(today), to: endOfMonth(today) };
        break;
      case "Last Month":
        const lastMonth = subMonths(today, 1);
        range = { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
        break;
      case "This Year":
        range = { from: startOfYear(today), to: endOfYear(today) };
        break;
      default:
        break;
    }

    onSelect?.(range, range.to!, {}, {} as React.MouseEvent);
  };

  return (
    <div className={cn("flex")}>
      {isRange && (
        <div className="w-[160px] h-[20.3rem] border-r pr-3 text-[10px] text-[#0079C2] pb-2 pt-4 pl-4 overflow-y-auto">
          <div className="space-y-1.5">
            {[
              "Reset",
              "Today",
              "This Week",
              "Last Week",
              "Last 7 Days",
              "Last 30 Days",
              "This Month",
              "Last Month",
              "This Year",
            ].map((label, idx) => (
              <button
                name={label}
                key={idx}
                onClick={() => handleQuickSelect(label)}
                className="w-full text-left pr-3 pl-6 flex items-center py-3 rounded border border-gray-300 hover:bg-blue-50 transition-colors duration-150"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="">
        <DayPicker
          {...props}
          showOutsideDays={showOutsideDays}
          numberOfMonths={1}
          className={cn("p-3", className)}
          classNames={{
            months: "flex flex-col gap-4",
            month: "space-y-4",
            caption: "relative flex justify-center items-center px-4",
            caption_label: "text-lg font-semibold",
            nav: "absolute top-0 inset-0 flex justify-between items-center px-2",
            nav_button:
              "h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:bg-gray-100 hover:text-black transition-colors duration-150",
            table: "w-full border-collapse",
            head_row: "flex mb-2",
            row: "flex w-full",
            head_cell:
              "text-center text-sm font-medium text-muted-foreground w-12",
            cell: "text-center text-base p-0 relative w-12 h-12",
            day: "h-12 w-12  rounded-md transition-colors duration-150 ease-in-out hover:bg-gray-100 hover:text-black aria-selected:bg-[#0079C2] aria-selected:text-white",
            day_selected:
              "bg-[#0079C2] text-white font-semibold pointer-events-auto",
            day_today: "border border-ring text-primary",
            day_outside: "text-muted-foreground opacity-50",
            day_disabled: "text-muted-foreground opacity-40",
            day_range_start: "bg-[#0079C2] text-white rounded-l-md",
            day_range_end: "bg-[#0079C2] text-white rounded-r-md",
            day_range_middle: "bg-blue-100 text-[#0079C2]",
            ...classNames,
          }}
          components={{
            IconLeft: ({
              className,
              ...props
            }: React.ComponentPropsWithoutRef<"svg">) => (
              <ChevronLeft className={cn("h-7 w-7", className)} {...props} />
            ),
            DayContent: ({ date, ...rest }) => (
              <div
                {...rest}
                data-day={date.getDate()}
                className="w-full h-full flex items-center justify-center"
              >
                {date.getDate()}
              </div>
            ),
            IconRight: ({
              className,
              ...props
            }: React.ComponentPropsWithoutRef<"svg">) => (
              <ChevronRight className={cn("h-7 w-7", className)} {...props} />
            ),
          }}
        />
      </div>
    </div>
  );
}

export { Calendar };
